// todesfallBelege.ts — Belege zum Todesfall (T-357): Arten und die reine
// Umrechnung zwischen Maske und Server.
//
// Bis 10/2026 gab es genau EIN Feld „Sterbeanzeige“ (`sterbeanzeigeUrl`). Ein
// zweiter Upload ersetzte den ersten — so ging an einer Akte die Sterbeurkunde
// verloren, als die Danksagung hochgeladen wurde. Jetzt: eine Liste, eine
// Zeile je Datei, die Art je Datei wählbar.
//
// BEWUSST OHNE REACT, aus demselben Grund wie `zuwendungenForm.ts`: Der
// Payload-Bau der Anlage und der Akte benutzt diese Funktionen.
//
// Die Codes spiegeln den Backend-Katalog
// (`apps/api/src/contactTodesfallBeleg.katalog.ts`) und sind Speicherwerte.

export const TODESFALL_BELEG_ARTEN = [
  { code: "STERBEANZEIGE", label: "Sterbeanzeige" },
  { code: "STERBEURKUNDE", label: "Sterbeurkunde" },
  { code: "KONDOLENZ", label: "Kondolenzschreiben" },
  { code: "DANKSAGUNG", label: "Danksagung" },
  { code: "ERBSCHEIN", label: "Erbschein/Testamentseröffnung" },
  { code: "SONSTIGES", label: "Sonstiges" },
] as const;

export type TodesfallBelegArt = (typeof TODESFALL_BELEG_ARTEN)[number]["code"];

const ARTEN = new Set<string>(TODESFALL_BELEG_ARTEN.map((a) => a.code));

/** Ein Beleg, wie Maske und Server ihn austauschen. */
export interface TodesfallBeleg {
  /** Server-Id — fehlt bei einem frisch hochgeladenen, noch nicht gespeicherten Beleg. */
  id?: number;
  art: TodesfallBelegArt;
  /** Freitext zur Art „Sonstiges“. */
  bezeichnung?: string | null;
  /** Media-Ref `/media/sterbeanzeigen/<datei>`. */
  url: string;
  /** Dateiname, unter dem der Beleg bei uns ankam. */
  name?: string | null;
}

export const todesfallBelegArtLabel = (art: string): string =>
  TODESFALL_BELEG_ARTEN.find((a) => a.code === art)?.label ?? "Sonstiges";

/** Unbekannte Art → „Sonstiges“ (wie im Backend). */
const alsArt = (v: unknown): TodesfallBelegArt => {
  const s = String(v ?? "").trim().toUpperCase();
  return (ARTEN.has(s) ? s : "SONSTIGES") as TodesfallBelegArt;
};

const textOderNull = (v: unknown): string | null => {
  const s = typeof v === "string" ? v.trim() : "";
  return s ? s : null;
};

/** Eine Zeile aus beliebiger Quelle (API-Antwort oder Formularfeld) säubern. */
const zeile = (z: unknown): TodesfallBeleg | null => {
  if (!z || typeof z !== "object") return null;
  const r = z as Record<string, unknown>;
  const url = typeof r.url === "string" ? r.url.trim() : "";
  if (!url) return null;
  const art = alsArt(r.art);
  const id = Number(r.id);
  return {
    ...(Number.isInteger(id) && id > 0 ? { id } : {}),
    art,
    bezeichnung: art === "SONSTIGES" ? textOderNull(r.bezeichnung) : null,
    url,
    name: textOderNull(r.name),
  };
};

/** Liste aus der API-Antwort (`Contact.todesfallBelege`) — Entfernte filtert schon der Server. */
export const todesfallBelegeAusApi = (roh: unknown): TodesfallBeleg[] =>
  Array.isArray(roh) ? roh.map(zeile).filter((z): z is TodesfallBeleg => z !== null) : [];

/** Liste aus einem Formularfeld (JSON-String der Anlage-Maske oder Array) — Unlesbares gilt als leer. */
export const todesfallBelegeAusFeld = (roh: unknown): TodesfallBeleg[] => {
  if (Array.isArray(roh)) return todesfallBelegeAusApi(roh);
  if (typeof roh !== "string" || !roh.trim()) return [];
  try {
    return todesfallBelegeAusApi(JSON.parse(roh));
  } catch {
    return [];
  }
};

/** Für den PUT/POST: genau die Felder, die der Abgleich im Backend kennt. */
export const todesfallBelegeFuerApi = (liste: TodesfallBeleg[]): Array<Record<string, unknown>> =>
  liste.map((b) => ({
    ...(b.id != null ? { id: b.id } : {}),
    art: b.art,
    bezeichnung: b.art === "SONSTIGES" ? (b.bezeichnung ?? null) : null,
    url: b.url,
    name: b.name ?? null,
  }));
